import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import snakecaseKeys from 'snakecase-keys';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  calculateSettlementRequest,
  createSettlementRequest,
  settlementSettingsRequest,
  showSettlementRequest,
  updateSettlementRequest
} from '../../../requests/payrollSettlements';
import { camelCaseEmptyStringRecursive } from '../../../services/utils';
import { DefaultModal, Icon, Loader, SimpleCenteredModal } from '../../../components';
import { indexBalancesRequest } from '../../../requests/balances';
import { resultFetchData } from '../helpers';
import SummaryDiscounts from './Form/SummaryDiscounts';
import { sendAlert } from '../../../actions/utils';
import SummaryHeader from './SummaryHeader';
import SubmitButton from './SubmitButton';
import TotalValues from './TotalValues';
import PendingRemunerations from '../Form/PendingRemunerations';
import SummaryCompensation from './SummaryCompensation';
import VariableAssets from './Form/VariableAssets';
import HolidayCompensation from './Form/HolidayCompensation';
import CurrentTitleTab from '../Tabs/CurrentTitleTab';
import SummaryWarning from './SummaryWarning';

const initialValues = settlement => {
  const { discountBalances } = settlement;
  return {
    settlement: {
      ...settlement,
      simulation: settlement.simulation,
      settlementBalancesAttributes: discountBalances || [],
      pendingRemunerationBalances: settlement?.pendingRemunerationBalances || [],
      variableAssets: settlement?.variableAssets || []
    },
    employee: settlement.employee,
    endDate: settlement.endDate,
    calculateSummary: false
  };
};

const prepareDataToSave = values => {
  const { settlement } = values;
  const allAssets = [
    ...settlement.settlementBalancesAttributes,
    ...settlement.pendingRemunerationBalances,
    ...settlement.variableAssets
  ];
  return {
    ...settlement,
    settlementBalancesAttributes: allAssets,
    updateSummary: true
  };
};

const validationSchema = () => {
  return Yup.object().shape({
    settlement: Yup.object().shape({
      settlementBalancesAttributes: Yup.array()
        .of(
          Yup.object().shape({
            amount: Yup.number()
              .required('El monto es requerido')
              .min(1, 'El monto debe ser mayor que cero'),
            balanceId: Yup.number().required('Concepto es requerido')
          })
        )
        .test('unique-balance-ids', 'Los descuentos no se pueden repetir', function validateUniqueBalanceIds(value) {
          const balanceIds = value.map(item => item.balanceId);
          return new Set(balanceIds).size === balanceIds.length;
        }),
      pendingRemunerationBalances: Yup.array()
        .of(
          Yup.object().shape({
            amount: Yup.number()
              .required('El monto es requerido')
              .min(1, 'El monto debe ser mayor que cero'),
            balanceId: Yup.number().required('Concepto es requerido')
          })
        )
        .test(
          'unique-balance-ids',
          'Los conceptos de remuneraciones pendientes no se pueden repetir',
          function validateUniqueBalanceIds(value) {
            const balanceIds = value.map(item => item.balanceId);
            return new Set(balanceIds).size === balanceIds.length;
          }
        ),
      variableAssets: Yup.array()
        .of(
          Yup.object().shape({
            balanceId: Yup.number().required('Concepto es requerido')
          })
        )
        .test('unique-balance-ids', 'Los conceptos variables no se pueden repetir', function validateUniqueBalanceIds(
          value
        ) {
          const balanceIds = value.map(item => item.balanceId);
          return new Set(balanceIds).size === balanceIds.length;
        })
        .test(
          'greater-than-zero',
          'El monto total de los haberes variables debe ser mayor que cero',
          function validateGreaterThanZero(value) {
            return value.reduce((acc, item) => {
              return (
                acc &&
                Number(item.antePenultimateMonthAmount) +
                  Number(item.penultimateMonthAmount) +
                  Number(item.lastMonthAmount) >
                  0
              );
            }, true);
          }
        )
    })
  });
};

export default function SettlementSummarySimulator({ viewOnly }) {
  const dispatch = useDispatch();
  const settlementState = JSON.parse(localStorage.getItem('settlementForm'));
  const [settlement, setSettlement] = useState(null);
  const [settlementSettings, setSettlementSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultModalShow, setDefaultModalShow] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [modalAction, setModalAction] = useState(() => null);
  const [modalActionParams, setModalActionParams] = useState({});
  const { id: settlementID } = useParams();
  const { currentCompany } = useSelector(state => state.auth);
  const [discounts, setDiscounts] = useState([]);
  const [assets, setAssets] = useState({
    variableAssets: [],
    fixedAssets: []
  });
  const history = useHistory();
  const fetchSettlement = useCallback(() => {
    setIsLoading(true);
    if (settlementID) {
      showSettlementRequest(settlementID, {
        dispatch,
        params: {
          update_summary: true
        },
        successCallback: ({ data }) => {
          setSettlement(camelCaseEmptyStringRecursive(data));
        },
        callback: () => {
          setIsLoading(false);
        }
      });
      return;
    }

    if (settlementState) {
      const newSummary = { ...settlementState };
      setSettlement(newSummary);
    }
  }, [dispatch, settlementID, settlementState]);

  const mapCodes = asset => ({
    ...asset,
    label: `${asset.name} - ${asset.code}`,
    value: asset.id
  });

  const fetchInitialBalance = useCallback(
    (type, setState) => {
      indexBalancesRequest({
        dispatch,
        params: {
          actives: true,
          filter_type: type,
          filter_legal: false,
          filter_settlement: true,
          sort_column: 'name',
          settlement_config: true,
          company_id: currentCompany.id,
          paginate: false
        },
        successCallback: response => {
          if (type === 'asset') {
            const allAssets = response.data.data;
            setAssets({
              variableAssets: allAssets.filter(asset => asset.asset_variable).map(mapCodes),
              fixedAssets: allAssets.filter(asset => !asset.asset_variable).map(mapCodes)
            });
            return;
          }
          const options = resultFetchData(response, type);
          setState(options);
        }
      });
    },
    [currentCompany.id, dispatch]
  );

  const fetchInitialDiscounts = useCallback(
    setState => {
      indexBalancesRequest({
        dispatch,
        params: {
          actives: true,
          filter_type: 'discount',
          filter_legal_discount: false,
          filter_settlement: true,
          sort_column: 'name',
          company_id: currentCompany.id,
          paginate: false
        },
        successCallback: response => {
          const options = resultFetchData(response);
          setState(options);
        }
      });
    },
    [currentCompany.id, dispatch]
  );

  const formRequest = ({ id, values, setSubmitting }) => {
    const newValues = prepareDataToSave(values);
    setModalShow(true);
    setDefaultModalShow(false);
    const snakedValues = { ...snakecaseKeys(newValues, { exclude: ['_destroy'] }) };
    const callbackActions = () => {
      setSubmitting(false);
      setModalShow(false);
      setDefaultModalShow(false);
    };
    const successCallbackActions = ({ data }) => {
      if (values?.calculateSummary) {
        dispatch(sendAlert({ message: 'Finiquito calculado correctamente', kind: 'success' }));
        setSettlement(camelCaseEmptyStringRecursive(data));
        return;
      }
      if (id) {
        dispatch(sendAlert({ message: 'Finiquito actualizado correctamente', kind: 'success' }));
        history.push(`/settlement_simulator`);
      }
      if (!id) {
        dispatch(sendAlert({ message: 'Finiquito creado correctamente', kind: 'success' }));
        history.push(`/settlement_simulator`);
      }
    };
    const haveToCalculate = values?.calculateSummary;
    if (haveToCalculate) {
      calculateSettlementRequest({
        dispatch,
        params: { settlement: { ...snakedValues } },
        successCallback: successCallbackActions,
        callback: callbackActions
      });
      return;
    }
    localStorage.removeItem('settlementForm');
    if (!id) {
      createSettlementRequest({
        dispatch,
        params: {
          settlement: {
            ...snakedValues
          }
        },
        successCallback: successCallbackActions,
        callback: callbackActions
      });
      return;
    }
    updateSettlementRequest(id, {
      dispatch,
      params: {
        settlement: {
          ...snakedValues
        }
      },
      successCallback: successCallbackActions,
      callback: callbackActions
    });
  };

  const beforeSubmit = (id, { values, formikBag }) => {
    const { setSubmitting } = formikBag;
    if (
      (values?.settlement?.simulation === 'false' || values?.settlement?.simulation === false) &&
      values?.calculateSummary === false
    ) {
      setSubmitting(false);
      setModalActionParams({ id, values, setSubmitting });
      setModalAction(() => formRequest);
      setDefaultModalShow(true);
      return;
    }
    formRequest({ id, values, setSubmitting });
  };

  const fetchSettlementSettings = () => {
    setIsLoading(true);
    settlementSettingsRequest({
      dispatch,
      params: { filter_by_company: currentCompany.id },
      successCallback: ({ data }) => {
        setSettlementSettings(data.data);
      },
      callback: () => {
        setIsLoading(false);
      }
    });
  };

  const initialFetch = () => {
    if (!currentCompany.id) return;
    fetchSettlement();
    fetchSettlementSettings();
    fetchInitialDiscounts(setDiscounts);
    fetchInitialBalance('asset', setAssets);
  };

  useEffect(initialFetch, [currentCompany.id]);
  return (
    <>
      {isLoading && (
        <div className="containerSpinnerLoad h-100 w-100">
          <Spinner animation="border" variant="primary" />
        </div>
      )}
      {settlement && !isLoading && (
        <Formik
          initialValues={initialValues(settlement)}
          validationSchema={validationSchema}
          onSubmit={(values, formikBag) => beforeSubmit(settlementID || values?.settlement?.id, { values, formikBag })}
          enableReinitialize
        >
          {formikProps => (
            <form onSubmit={formikProps.handleSubmit}>
              <Row>
                <Col style={{ backgroundColor: '#f5f5f5', marginTop: 35 }} xl={12}>
                  <Row className="mt-2 mb-2">
                    <Col md={1}>
                      <Button variant="circle-dark" className="btn-circle mt-2" block onClick={() => history.goBack()}>
                        <Icon icon="chevron-back" />
                      </Button>
                    </Col>
                    <h2 className="mt-2 text-center"> SIMULADOR DE FINIQUITOS </h2>
                  </Row>
                  <CurrentTitleTab
                    title="RESUMEN SIMULADOR DE FINIQUITOS"
                    customStyle={{
                      textAlign: 'left',
                      paddingTop: 2,
                      paddingBottom: 2
                    }}
                  />
                  <SummaryHeader settlement={settlement} />
                  <CurrentTitleTab
                    title="DETALLE CALCULO"
                    customStyle={{
                      textAlign: 'left',
                      paddingTop: 2,
                      paddingBottom: 2
                    }}
                  />
                  <Card>
                    <Card.Body>
                      <HolidayCompensation settlementSettings={settlementSettings} viewOnly={viewOnly} />
                      <VariableAssets assets={assets} viewOnly={viewOnly} />
                      <PendingRemunerations assets={assets} viewOnly={viewOnly} />
                      <hr className="mb-2 mt-2 hr-summary-index" />
                      <SummaryDiscounts discounts={discounts} dispatch={dispatch} viewOnly={viewOnly} />
                      <SummaryCompensation settlement={settlement} viewOnly={viewOnly} />
                      <TotalValues />
                      {viewOnly ? null : <SubmitButton />}
                    </Card.Body>
                  </Card>
                </Col>
                <DefaultModal
                  title="Advertencia"
                  body={<SummaryWarning setModalActionParams={setModalActionParams} />}
                  show={defaultModalShow}
                  modalSize="md"
                  handleClose={() => setDefaultModalShow(false)}
                  handleConfirm={() => modalAction(modalActionParams)}
                />
                <SimpleCenteredModal
                  title="Advertencia"
                  body={<Loader message="Estamos procesando su solicitud, por favor espera unos segundos." />}
                  show={modalShow}
                  onHide={() => setModalShow(false)}
                  bodyClass="mx-3 mb-3"
                  size="md"
                  closeButton={false}
                />
              </Row>
            </form>
          )}
        </Formik>
      )}
    </>
  );
}
